/**
 * What the diner is told above the ordering surface while it cannot be shown
 * (ORDR-42): loading, an unknown venue, or a flow that failed.
 *
 * Payment notices moved into the app's own Checkout (ORDR-166), where the
 * diner is when they apply.
 *
 * REFUSED SESSION (ORDR-219). Commerce refused this browser's session the menu
 * or the cart: it is signed in to another business. The diner is told so and
 * offered signing out through the app's one sign-out (`../auth/signOut.ts`).
 * Once the session has ended, THIS page is loaded again, so the menu is read
 * with no session; a failed sign-out says the diner is still signed in.
 */

import { useCallback, useState, type ReactElement } from "react";
import { Button } from "@pirouz-ca/ui-components";
import type { ApiClient } from "../api/client.js";
import { signOut } from "../auth/signOut.js";
import type { CustomerOrderingStatus } from "./useCustomerOrdering.js";

const NOTICE = "m-3 rounded-[var(--border-radius-md)] bg-[var(--color-neutral-100)] p-3 text-sm";

export interface CustomerStatusProps {
  readonly status: CustomerOrderingStatus;
  /** For signing out of a refused session. */
  readonly client: ApiClient;
  /** A document load. */
  readonly navigate: (href: string) => void;
}

function currentHref(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function SessionRefused({ client, navigate }: Pick<CustomerStatusProps, "client" | "navigate">): ReactElement {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const onSignOut = useCallback((): void => {
    setPending(true);
    setFailure(null);
    void signOut(client).then((outcome) => {
      if (outcome.kind === "signed-out") {
        navigate(currentHref());
      } else {
        setPending(false);
        setFailure(outcome.correlationId);
      }
    });
  }, [client, navigate]);

  return (
    <div role="alert" data-testid="customer-session-refused" className={`${NOTICE} flex flex-col items-start gap-2`}>
      <p>
        This menu cannot be shown because this browser is signed in to another business. Sign out to see this menu and
        order.
      </p>
      <Button type="button" variant="primary" disabled={pending} onClick={onSignOut} className="min-h-11 px-4">
        Sign out
      </Button>
      {failure !== null && (
        <p data-testid="customer-sign-out-error">We could not sign you out. You are still signed in. Reference: {failure}</p>
      )}
    </div>
  );
}

const PLACEHOLDER_CHIPS = ["w-20", "w-24", "w-16"] as const;
const PLACEHOLDER_ROWS = [
  ["w-2/5", "w-4/5"],
  ["w-1/2", "w-3/5"],
  ["w-1/3", "w-2/3"],
  ["w-2/5", "w-1/2"],
] as const;
const SHAPE = "rounded-full bg-[var(--color-neutral-200)] motion-safe:animate-pulse";

/**
 * The menu's own shape while it is read (ORDR-227): section chips and item
 * rows laid out where the menu will stand, so the page reads as a menu on its
 * way rather than a page with nothing on it (the operator took the old single
 * line for no menu). The shapes are decoration and hidden from assistive
 * technology; a screen reader hears one sentence, and `aria-busy` says the
 * region is still filling.
 */
function MenuPlaceholder(): ReactElement {
  return (
    <div role="status" aria-busy="true" data-testid="customer-loading" className="flex flex-col">
      <span className="sr-only">Loading the menu</span>
      <div aria-hidden="true">
        <div className="flex gap-2 px-5 py-3">
          {PLACEHOLDER_CHIPS.map((width) => (
            <span key={width} data-placeholder="chip" className={`h-11 shrink-0 ${width} ${SHAPE}`} />
          ))}
        </div>
        <ul className="m-0 flex list-none flex-col p-0">
          {PLACEHOLDER_ROWS.map(([name, description]) => (
            <li key={`${name}-${description}`} data-placeholder="row" className="flex flex-col gap-2 border-t border-[var(--color-neutral-100)] px-5 py-3.5">
              <span className={`h-4 ${name} ${SHAPE}`} />
              <span className={`h-3 ${description} ${SHAPE}`} />
              <span className={`h-3 w-14 ${SHAPE}`} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function CustomerStatus({ status, client, navigate }: CustomerStatusProps): ReactElement | null {
  if (status.kind === "session-refused") {
    return <SessionRefused client={client} navigate={navigate} />;
  }
  if (status.kind === "loading") {
    return <MenuPlaceholder />;
  }
  if (status.kind === "not-found") {
    return (
      <p role="alert" data-testid="customer-site-unknown" className={NOTICE}>
        We could not find ordering at this venue. Scan the code at your venue again, or ask a member of staff.
      </p>
    );
  }
  if (status.kind === "failed") {
    return (
      <p role="alert" data-testid="customer-flow-failed" className={NOTICE}>
        Ordering is not available here just now. Nothing has been charged.
        {status.correlationId !== null && ` Reference: ${status.correlationId}`}
      </p>
    );
  }
  return null;
}
